import { PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { styled } from '@mui/material';
import dayjs from 'dayjs';
import { LinkProps } from 'react-router-dom';

import { DATE_TIME } from '@cast/constants';
import {
  Chip,
  Column,
  EllipsisWithTooltip,
  Icons,
  Paginator,
  TableActionButton,
  TableBulkActions,
  TableHeader,
  TableRecordsCount,
  Tooltip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { Notification } from '@cast/types';

import { Link } from 'components/router';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { useAbility } from '@cast/shared-ability-data-access';
import { useAckNotificationsMutation } from 'hooks/mutations/notifications';
import { useGetWebhookCategoriesQuery } from 'hooks/queries/notifications';

import { NoNotifications, SeverityChip } from '../../_components';
import { useNotificationDrawer } from '../../hooks/useNotificationDrawer';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoResults,
  SearchableTable,
  useSearchContext,
} from '@cast/shared-search-ui';

const StyledLink = styled(Link)<PropsWithChildren<LinkProps>>(({ theme }) => ({
  ...theme.typography.P12R,
  color: tokens.theme.fg.action.primary,
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'underline',
  },
}));

export type NotificationsTableProps = {
  pageSize?: number;
  currentPage?: number;
  count?: number;
};

export const NotificationsTable = ({
  currentPage,
  count,
  pageSize,
}: NotificationsTableProps) => {
  const { openNotificationDrawer } = useNotificationDrawer();
  const [selected, setSelected] = useState<string[]>([]);
  const searchContext = useSearchContext<Notification>();
  const ackNotificationsMutation = useAckNotificationsMutation(() =>
    setSelected([])
  );
  const canEdit = useAbility().can('update', 'OrganizationNotifications');
  const { categories } = useGetWebhookCategoriesQuery();
  const allIds = useMemo(
    () => searchContext?.data?.map((item) => item.id + '') || [],
    [searchContext?.data]
  );

  const handleSelectedRows = useCallback(
    (keys: string[]) => {
      if (!keys.length) {
        if (selected.length) {
          setSelected([]);
        } else {
          if (allIds?.length) {
            setSelected(allIds);
          }
        }
      } else {
        setSelected(keys);
      }
    },
    [allIds, selected.length]
  );

  const paginatorVisible =
    !searchContext?.isLoading &&
    currentPage !== undefined &&
    pageSize !== undefined &&
    count !== undefined;

  return (
    <SearchableTable
      cacheKey={tableCacheKeys.NOTIFICATIONS}
      testId="notifications-table"
      urlKey="notifications"
      maxHeight="calc(100vh - 240px)"
      selectedRows={canEdit ? selected : undefined}
      onRowsSelected={handleSelectedRows}
      components={{
        noData: <NoNotifications />,
        noResults: (
          <NoResults icon={<Icons.BellSimpleRinging />} entity="notification" />
        ),
        failed: <FailedToLoad title="Failed to load notifications" />,
      }}
      getRowClass={(row: Notification) =>
        row.ackAt ? 'resolved-notification' : undefined
      }
      onRowClick={(_, { row }) => {
        if (!searchContext?.isLoading) {
          openNotificationDrawer({ notification: row });
        }
      }}
      rowKey={(row: Notification) => row.id!}
      outerHeader={
        !!searchContext?.data?.length && (
          <TableHeader
            recordsCount={
              <TableRecordsCount
                count={selected.length}
                outOf={count}
                title="notification"
              />
            }
            bulkActions={
              <TableBulkActions visible={!!selected.length}>
                <TableActionButton
                  onClick={() => {
                    if (ackNotificationsMutation.isPending) {
                      return;
                    }
                    ackNotificationsMutation.mutate(selected);
                  }}
                  startIcon={
                    selected?.length === allIds.length ? (
                      <Icons.Checks />
                    ) : (
                      <Icons.CheckSquare />
                    )
                  }
                  disabled={!canEdit}
                  testId="resolve-selected-button"
                >
                  {selected?.length === allIds.length
                    ? 'Resolve all'
                    : 'Resolve selected'}
                </TableActionButton>
              </TableBulkActions>
            }
            paginator={
              paginatorVisible && (
                <Paginator
                  recordsCount={count}
                  pageSize={pageSize}
                  currentPage={currentPage + 1}
                  onCurrentPageChange={(page) => {
                    if (page > currentPage) {
                      searchContext?.fetchNextPage?.();
                    }
                    if (page <= currentPage) {
                      searchContext?.fetchPreviousPage?.();
                    }
                  }}
                />
              )
            }
          />
        )
      }
      sx={{
        width: '100%',

        '& .DS-Table-BodyRow': {
          cursor: 'pointer',
        },

        '& .resolved-notification': {
          background: tokens.theme.bg.structural.surfaceSubtle,
        },

        '& .resolved-notification .DS-Table-BodyRow:hover': {
          background: tokens.theme.bg.action.surfaceHover,
        },
      }}
    >
      <Column<Notification>
        id="name"
        header="Name"
        minWidth={256}
        renderCell={({ name, ackAt }) => (
          <EllipsisWithTooltip
            color={tokens.theme.fg.action.primary}
            TypographyProps={{ variant: ackAt ? 'P12R' : 'P12B' }}
            fontWeight={!ackAt ? 700 : undefined}
          >
            {name}
          </EllipsisWithTooltip>
        )}
        sortable
        resizable
      />
      <Column<Notification>
        id="clusterName"
        header="Cluster"
        minWidth={240}
        renderCell={({ clusterMetadata }) => {
          if (!clusterMetadata) {
            return null;
          }
          return (
            <Stack>
              <StyledLink
                target="_blank"
                rel="noreferrer noopener"
                onClick={(e) => e.stopPropagation()}
                to={`/external-clusters/${clusterMetadata.id}`}
              >
                <Typography variant="P12R" noWrap>
                  {clusterMetadata.name}
                </Typography>
              </StyledLink>
              {clusterMetadata.project && (
                <Typography
                  variant="P10R"
                  color={tokens.theme.fg.structural.secondary}
                  noWrap
                  mt={-4}
                >
                  {clusterMetadata.project}
                </Typography>
              )}
            </Stack>
          );
        }}
        sortable
      />
      <Column<Notification>
        id="operationType"
        header="Operation"
        renderCell={({ operationMetadata }) => (
          <Typography variant="P12R">{operationMetadata?.type}</Typography>
        )}
        minWidth={200}
        sortable
      />
      <Column<Notification>
        id="category"
        header="Category"
        renderCell={({ operationMetadata }) => {
          const raw = operationMetadata?.category;
          const displayName = raw
            ? categories?.find((c) => c.value === raw)?.name ?? raw
            : 'Other';
          return <Typography variant="P12R">{displayName}</Typography>;
        }}
        minWidth={160}
      />
      <Column<Notification>
        id="timestamp"
        header="Timestamp"
        renderCell={({ timestamp, isExpired }) => (
          <Stack direction="row" alignItems="center" gap={8}>
            <Typography variant="P12R">
              {dayjs(timestamp).format(DATE_TIME)}
            </Typography>
            {isExpired && (
              <Tooltip title="Obsolete notification" placement="bottom" arrow>
                <Chip
                  iconChip
                  startIcon={<Icons.Clock size={12} />}
                  size="sm"
                  sx={{
                    color: tokens.theme.fg.status.onNeutralSubtle,
                    backgroundColor: tokens.theme.bg.status.neutralSubtle,
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        )}
        minWidth={240}
        sortable
        defaultSort="desc"
      />
      <Column<Notification>
        id="severity"
        header="Severity"
        minWidth={160}
        renderCell={({ severity }) => (
          <SeverityChip severity={severity || ''} size="small" />
        )}
        sortable
      />
    </SearchableTable>
  );
};
